/**
 * ProposalsPanel — list of Copilot proposals with approve / reject / publish.
 * Used in the dedicated /copilot page; teachers also see their own only.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, X, Send, Sparkles, FileText, ClipboardCheck, Pencil, Eye, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import CopilotBadge from './CopilotBadge';

const TYPE_ICONS = {
  pop_quiz: ClipboardCheck,
  weekly_recap_quiz: ClipboardCheck,
  lesson_plan: FileText,
  bible_quiz: ClipboardCheck,
  bible_reading_plan: FileText,
  syllabus: FileText,
  assignment: FileText,
  mock_exam: ClipboardCheck,
};

export const ProposalsPanel = ({ tenantId }) => {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('review_required');
  const [selected, setSelected] = useState(null);
  const [rejectDlg, setRejectDlg] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // iter-366 edit-inline + dry-run state
  const [editingDraft, setEditingDraft] = useState(null); // null = preview-only; obj = current edit buffer
  const [dryRunResult, setDryRunResult] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['copilot-proposals', tenantId, statusFilter],
    queryFn: () => api.get('/copilot/proposals', { params: {
      tenant_id: tenantId,
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    }}).then(r => r.data),
    enabled: !!tenantId,
  });

  const proposals = data?.proposals || [];

  const refresh = () => qc.invalidateQueries({ queryKey: ['copilot-proposals', tenantId] });

  const approveMut = useMutation({
    mutationFn: (id) => api.post(`/copilot/proposals/${id}/approve`, {}, { params: { tenant_id: tenantId }}).then(r => r.data),
    onSuccess: (d) => { toast.success('Approved. Ready to publish.'); setSelected(d); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const publishMut = useMutation({
    mutationFn: (id) => api.post(`/copilot/proposals/${id}/publish`, {}, { params: { tenant_id: tenantId }}).then(r => r.data),
    onSuccess: (d) => { toast.success(`Published ${d.published_refs?.length || 0} item(s)`); setSelected(d.proposal); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const rejectMut = useMutation({
    mutationFn: () => api.post(`/copilot/proposals/${rejectDlg.id}/reject`,
      { reason: rejectReason },
      { params: { tenant_id: tenantId }}).then(r => r.data),
    onSuccess: () => { toast.success('Rejected'); setRejectDlg(null); setRejectReason(''); refresh(); setSelected(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // iter-366 — patch payload before approval
  const patchMut = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/copilot/proposals/${id}`,
      { payload_patch: patch },
      { params: { tenant_id: tenantId }}).then(r => r.data),
    onSuccess: (d) => { toast.success('Edits saved'); setSelected(d); setEditingDraft(null); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const dryRunMut = useMutation({
    mutationFn: (id) => api.post(`/copilot/proposals/${id}/dry-run`, {}, { params: { tenant_id: tenantId }}).then(r => r.data),
    onSuccess: (d) => setDryRunResult(d),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Whenever we select a different proposal, exit edit mode + clear dry-run.
  const handleSelect = (p) => { setSelected(p); setEditingDraft(null); setDryRunResult(null); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4" data-testid="copilot-proposals-panel">
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-600" /> Proposals
          </CardTitle>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-7 w-full max-w-[140px] text-xs" data-testid="copilot-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="review_required">Awaiting review</SelectItem>
              <SelectItem value="teacher_approved">Ready to publish</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="space-y-1 max-h-[640px] overflow-auto">
          {isLoading && <p className="text-xs text-slate-400">Loading…</p>}
          {!isLoading && !proposals.length && <p className="text-xs text-slate-400 text-center py-6" data-testid="copilot-proposals-empty">No proposals yet.</p>}
          {proposals.map(p => {
            const Icon = TYPE_ICONS[p.type] || Sparkles;
            const active = selected?.id === p.id;
            return (
              <button key={p.id}
                onClick={() => handleSelect(p)}
                data-testid={`copilot-proposal-row-${p.id}`}
                className={`w-full text-left p-2 rounded border text-xs transition ${active ? 'bg-violet-50 border-violet-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <Icon className="h-3.5 w-3.5 text-violet-600" />
                  <span className="font-medium truncate flex-1">{p.payload?.title || p.type}</span>
                  <Badge variant="outline" className="text-[9px]">{p.type}</Badge>
                </div>
                <p className="text-slate-500 truncate">{p.rationale}</p>
                <CopilotBadge status={p.status} className="mt-1" />
              </button>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{selected ? selected.payload?.title || selected.type : 'Select a proposal'}</CardTitle>
        </CardHeader>
        <CardContent>
          {!selected ? (
            <p className="text-sm text-slate-400">Pick a proposal from the list to review the full draft.</p>
          ) : (
            <div className="space-y-3" data-testid={`copilot-proposal-detail-${selected.id}`}>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <CopilotBadge status={selected.status} />
                <Badge variant="outline" className="text-[10px]">{selected.type}</Badge>
                <span className="text-slate-500">Created {new Date(selected.created_at).toLocaleString()}</span>
              </div>
              {selected.rationale && <p className="text-xs text-slate-500 italic">Why: {selected.rationale}</p>}
              {selected.source && (
                <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                  <span className="font-semibold">Source: </span>
                  {Object.entries(selected.source).filter(([, v]) => v).slice(0, 4).map(([k, v]) =>
                    <span key={k} className="mr-2">{k}: {Array.isArray(v) ? v.join(', ') : String(v)}</span>)}
                </div>
              )}
              {editingDraft ? (
                <InlineEditor
                  type={selected.type}
                  draft={editingDraft}
                  setDraft={setEditingDraft}
                  onSave={() => patchMut.mutate({ id: selected.id, patch: editingDraft })}
                  onCancel={() => setEditingDraft(null)}
                  saving={patchMut.isPending}
                />
              ) : (
                <PayloadPreview type={selected.type} payload={selected.payload} />
              )}

              {dryRunResult && (
                <div className="border rounded p-2 bg-amber-50 border-amber-200 text-xs" data-testid="copilot-dryrun-result">
                  <div className="flex items-center gap-1 font-semibold text-amber-800">
                    <Eye className="h-3.5 w-3.5" /> Dry run preview
                  </div>
                  <p className="text-amber-700 mt-1">If you publish now, these rows will be created:</p>
                  <ul className="ml-4 list-disc text-amber-900">
                    {(dryRunResult.would_create || []).map((w, i) => (
                      <li key={i}><strong>{w.collection}</strong> — {w.count} row{w.count === 1 ? '' : 's'}</li>
                    ))}
                  </ul>
                  {(dryRunResult.warnings || []).length > 0 && (
                    <div className="mt-1 flex items-start gap-1 text-rose-700">
                      <AlertTriangle className="h-3 w-3 mt-0.5" />
                      <ul className="ml-1 list-disc">
                        {dryRunResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  )}
                  <Button size="sm" variant="ghost" className="mt-1 h-6 text-xs" onClick={() => setDryRunResult(null)}>Hide</Button>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {(selected.status === 'review_required' || selected.status === 'draft' || selected.status === 'edited') && !editingDraft && (
                  <>
                    <Button size="sm" onClick={() => approveMut.mutate(selected.id)}
                      disabled={approveMut.isPending}
                      data-testid={`copilot-approve-${selected.id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingDraft(JSON.parse(JSON.stringify(selected.payload || {})))}
                      data-testid={`copilot-edit-${selected.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => dryRunMut.mutate(selected.id)}
                      disabled={dryRunMut.isPending}
                      data-testid={`copilot-dryrun-${selected.id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> Dry run
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRejectDlg(selected)}
                      data-testid={`copilot-reject-${selected.id}`}>
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  </>
                )}
                {selected.status === 'teacher_approved' && (
                  <Button size="sm" onClick={() => publishMut.mutate(selected.id)}
                    disabled={publishMut.isPending}
                    data-testid={`copilot-publish-${selected.id}`}>
                    <Send className="h-3.5 w-3.5 mr-1" /> Publish to ecosystem
                  </Button>
                )}
                {selected.status === 'published' && (
                  <p className="text-xs text-emerald-700">
                    Published {selected.published_refs?.length || 0} item(s) into the ecosystem.
                    {selected.type !== 'lesson_plan' && ' Find them in the Question Bank, tagged ai_copilot.'}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!rejectDlg} onOpenChange={(o) => !o && setRejectDlg(null)}>
        <DialogContent data-testid="copilot-reject-dialog">
          <DialogHeader><DialogTitle>Reject proposal</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs">Reason (audit log)</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3}
              placeholder="e.g. Not aligned with current syllabus; revise from Lesson 3 only."
              data-testid="copilot-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDlg(null)}>Cancel</Button>
            <Button disabled={rejectReason.trim().length < 3 || rejectMut.isPending}
              onClick={() => rejectMut.mutate()}
              data-testid="copilot-reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


const PayloadPreview = ({ type, payload }) => {
  if (!payload) return null;
  if (type === 'pop_quiz' || type === 'weekly_recap_quiz' || type === 'bible_quiz' || type === 'mock_exam') {
    const questions = payload.questions || [];
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-3 text-xs text-slate-600 font-medium">
          <span>Total marks: {payload.total_marks}</span>
          <span>Difficulty: {payload.difficulty}</span>
          <span>Questions: {questions.length}</span>
        </div>
        <ol className="space-y-2 list-decimal pl-5 text-sm">
          {questions.map((q, i) => (
            <li key={i} className="text-slate-800">
              <p>{q.question_text}
                <span className="ml-2 text-[10px] text-slate-500">[{q.question_type} · {q.marks}m · {q.difficulty}]</span>
              </p>
              {q.options?.length > 0 && (
                <ul className="mt-1 ml-1 text-xs text-slate-600 space-y-0.5">
                  {q.options.map((o, j) => <li key={j}>– {o}</li>)}
                </ul>
              )}
              {q.correct_answer && <p className="text-[11px] text-emerald-700 mt-0.5">Answer: {q.correct_answer}</p>}
              {q.explanation && <p className="text-[11px] text-slate-500 mt-0.5 italic">Why: {q.explanation}</p>}
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (type === 'lesson_plan') {
    return (
      <div className="space-y-2 text-sm">
        {payload.learning_objectives?.length > 0 && (
          <div><span className="font-semibold">Learning objectives:</span>
            <ul className="ml-4 list-disc text-slate-700">{payload.learning_objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
          </div>
        )}
        {payload.introduction && <div><span className="font-semibold">Introduction:</span> <p className="text-slate-700">{payload.introduction}</p></div>}
        {payload.main_teaching_points?.length > 0 && (
          <div><span className="font-semibold">Main teaching points:</span>
            <ul className="ml-4 list-disc text-slate-700">{payload.main_teaching_points.map((o, i) => <li key={i}>{o}</li>)}</ul>
          </div>
        )}
        {payload.activities?.length > 0 && (
          <div><span className="font-semibold">Activities:</span>
            <ul className="ml-4 list-disc text-slate-700">
              {payload.activities.map((a, i) => <li key={i}><strong>{a.name}:</strong> {a.description} ({a.duration_minutes}m)</li>)}
            </ul>
          </div>
        )}
        {payload.homework && <div><span className="font-semibold">Homework:</span> <p className="text-slate-700">{payload.homework}</p></div>}
      </div>
    );
  }
  if (type === 'bible_reading_plan') {
    const days = payload.daily_readings || [];
    return (
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-3 text-xs text-slate-600 font-medium">
          <span>Theme: {payload.theme}</span>
          <span>Duration: {payload.duration_days} days</span>
          <span>Audience: {payload.audience}</span>
          <span>{payload.bible_version}</span>
        </div>
        {payload.description && <p className="text-slate-700 text-xs italic">{payload.description}</p>}
        <ol className="space-y-2 list-decimal pl-5">
          {days.map((d, i) => (
            <li key={i} className="text-slate-800">
              <p className="font-medium">{d.title} <span className="text-[10px] text-slate-500 ml-1">[Day {d.day}]</span></p>
              <p className="text-xs text-slate-700">{d.passage_reference}</p>
              {d.focus_verse && <p className="text-[11px] text-emerald-700">Focus: {d.focus_verse}</p>}
              {d.reflection_prompt && <p className="text-[11px] text-slate-500 italic mt-0.5">Reflect: {d.reflection_prompt}</p>}
              {d.memory_verse && <p className="text-[11px] text-violet-700">Memorise: {d.memory_verse}</p>}
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (type === 'syllabus') {
    const weeks = payload.weekly_breakdown || [];
    return (
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-3 text-xs text-slate-600 font-medium flex-wrap">
          <span>Subject: {payload.subject}</span>
          <span>{payload.year_group}</span>
          <span>{payload.term}</span>
          <span>{payload.duration_weeks} weeks</span>
        </div>
        {payload.overview && <p className="text-xs text-slate-700 italic">{payload.overview}</p>}
        {payload.learning_outcomes?.length > 0 && (
          <div><span className="font-semibold">Learning outcomes:</span>
            <ul className="ml-4 list-disc text-slate-700">{payload.learning_outcomes.map((o, i) => <li key={i}>{o}</li>)}</ul>
          </div>
        )}
        <div><span className="font-semibold">Weekly breakdown:</span>
          <ol className="space-y-1 list-decimal pl-5">
            {weeks.map((w, i) => (
              <li key={i} className="text-slate-700">
                <span className="font-medium">Week {w.week}: {w.topic}</span>
                {w.subtopics?.length > 0 && <span className="text-slate-500 text-xs ml-1">({w.subtopics.join(', ')})</span>}
              </li>
            ))}
          </ol>
        </div>
      </div>
    );
  }
  if (type === 'assignment') {
    const rubric = payload.rubric || [];
    return (
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-3 text-xs text-slate-600 font-medium flex-wrap">
          <span>Max marks: {payload.max_marks}</span>
          <span>Difficulty: {payload.difficulty}</span>
          <span>Format: {payload.submission_format}</span>
          {payload.estimated_minutes && <span>~{payload.estimated_minutes}m</span>}
        </div>
        {payload.instructions && <div><span className="font-semibold">Instructions:</span><p className="text-slate-700 whitespace-pre-wrap">{payload.instructions}</p></div>}
        {rubric.length > 0 && (
          <div><span className="font-semibold">Rubric:</span>
            <ul className="ml-4 list-disc text-slate-700 text-xs">
              {rubric.map((r, i) => (
                <li key={i}>
                  <strong>{r.criterion}</strong> ({r.weight_percent}%) — Full: {r.descriptor_full_marks}
                </li>
              ))}
            </ul>
          </div>
        )}
        {payload.expected_answer_guidance && (
          <div className="text-[11px] text-slate-500 italic"><strong>Teacher guidance:</strong> {payload.expected_answer_guidance}</div>
        )}
      </div>
    );
  }
  return <pre className="text-xs bg-slate-50 p-2 rounded overflow-auto">{JSON.stringify(payload, null, 2)}</pre>;
};


// ---------------------------------------------------------------------------
// iter-366 — InlineEditor: lets admins rename the title, drop questions,
// swap difficulty, etc. without leaving the proposals panel.
// Supports quiz-shaped types (questions + difficulty + title) explicitly
// and falls back to a raw JSON editor for everything else.
// ---------------------------------------------------------------------------

const InlineEditor = ({ type, draft, setDraft, onSave, onCancel, saving }) => {
  const isQuizShaped = ['pop_quiz', 'weekly_recap_quiz', 'bible_quiz', 'mock_exam'].includes(type);

  if (isQuizShaped) {
    const questions = draft.questions || [];
    return (
      <div className="space-y-3 border-l-2 border-violet-400 pl-3" data-testid="copilot-inline-editor">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-violet-700 uppercase tracking-wider">Edit draft</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={onSave} disabled={saving} data-testid="copilot-inline-save">
              {saving ? 'Saving…' : 'Save edits'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-2">
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={draft.title || ''}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              data-testid="copilot-inline-title"
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Default difficulty</Label>
            <Select value={draft.difficulty || 'mixed'} onValueChange={(v) => setDraft({ ...draft, difficulty: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="copilot-inline-difficulty">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
                <SelectItem value="mixed">Mixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs">Questions ({questions.length})</Label>
            <span className="text-[10px] text-slate-400">Click Drop to remove a question. Tweak difficulty per-question.</span>
          </div>
          <ol className="space-y-2 list-decimal pl-5">
            {questions.map((q, i) => (
              <li key={i} className="bg-slate-50 border border-slate-200 rounded p-2" data-testid={`copilot-inline-q-${i}`}>
                <Textarea
                  value={q.question_text || ''}
                  onChange={(e) => {
                    const next = [...questions];
                    next[i] = { ...next[i], question_text: e.target.value };
                    setDraft({ ...draft, questions: next });
                  }}
                  rows={2}
                  className="text-xs"
                  data-testid={`copilot-inline-q-text-${i}`}
                />
                <div className="flex items-center gap-2 mt-1.5">
                  <Select value={q.difficulty || 'medium'} onValueChange={(v) => {
                    const next = [...questions];
                    next[i] = { ...next[i], difficulty: v };
                    setDraft({ ...draft, questions: next });
                  }}>
                    <SelectTrigger className="h-7 text-[11px] w-full max-w-[100px]" data-testid={`copilot-inline-q-diff-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-[10px] text-slate-500">{q.question_type} · {q.marks}m</span>
                  {q.correct_answer && (
                    <Input
                      value={q.correct_answer || ''}
                      onChange={(e) => {
                        const next = [...questions];
                        next[i] = { ...next[i], correct_answer: e.target.value };
                        setDraft({ ...draft, questions: next });
                      }}
                      className="h-7 text-[11px] flex-1"
                      placeholder="Correct answer"
                      data-testid={`copilot-inline-q-answer-${i}`}
                    />
                  )}
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:bg-rose-50"
                    onClick={() => {
                      const next = questions.filter((_, j) => j !== i);
                      setDraft({ ...draft, questions: next });
                    }}
                    data-testid={`copilot-inline-q-drop-${i}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Drop
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    );
  }

  // Generic fallback: title-only edit + raw JSON for the rest.
  return (
    <div className="space-y-2 border-l-2 border-violet-400 pl-3" data-testid="copilot-inline-editor">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-violet-700 uppercase tracking-wider">Edit draft</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={onSave} disabled={saving} data-testid="copilot-inline-save">
            {saving ? 'Saving…' : 'Save edits'}
          </Button>
        </div>
      </div>
      <Label className="text-xs">Title</Label>
      <Input
        value={draft.title || ''}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        className="h-8 text-sm"
        data-testid="copilot-inline-title"
      />
      <Label className="text-xs">Raw JSON (advanced)</Label>
      <Textarea
        rows={10}
        value={JSON.stringify(draft, null, 2)}
        onChange={(e) => {
          try {
            setDraft(JSON.parse(e.target.value));
          } catch {
            /* ignore — user is still typing */
          }
        }}
        className="text-xs font-mono"
        data-testid="copilot-inline-json"
      />
    </div>
  );
};

export default ProposalsPanel;
